// ═══════════════════════════════════════════════════════════════
// RollCall — Personnel Badges with QR codes
// Select, preview, print, and PDF export
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Button, Checkbox, TextInput, Select,
} from '@mantine/core';
import {
  IconPrinter, IconFileTypePdf, IconArrowLeft, IconUsers, IconId,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import { notifications } from '@mantine/notifications';
import { useRollCallStore } from '../../store/rollcallStore';

const BADGE_STYLES = [
  { value: 'standard', label: 'Standard (3.5" x 2.25")' },
  { value: 'large', label: 'Large (4" x 3")' },
];

const BADGE_DIMS: Record<string, { w: number; h: number; qr: number; fontSize: { name: number; detail: number; org: number } }> = {
  standard: { w: 85.6, h: 54, qr: 28, fontSize: { name: 11, detail: 7, org: 6 } },
  large: { w: 101.6, h: 76.2, qr: 36, fontSize: { name: 13, detail: 8, org: 7 } },
};

interface BadgeCardProps {
  person: { firstName: string; lastName: string; agency: string; position: string; pin: string; badgeId: string };
  style?: string;
  qrDataUrl?: string;
}

function BadgeCard({ person, style = 'standard', qrDataUrl }: BadgeCardProps) {
  const isLarge = style === 'large';
  return (
    <div className="badge-card" style={{
      width: isLarge ? 384 : 324, height: isLarge ? 288 : 216,
      border: '2px solid #112D4E', borderRadius: 12, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', background: '#fff',
      fontFamily: "'DM Sans', sans-serif", position: 'relative',
      pageBreakInside: 'avoid', breakInside: 'avoid',
    }}>
      {/* Header band */}
      <div style={{
        background: 'linear-gradient(135deg, #1a365d 0%, #3F72AF 100%)',
        padding: isLarge ? '12px 16px' : '8px 14px',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <IconId size={isLarge ? 16 : 13} color="#fff" />
        <span style={{ color: '#fff', fontSize: isLarge ? 12 : 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>RollCall</span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: isLarge ? 10 : 8, marginLeft: 'auto' }}>Emergency Attendance</span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', padding: isLarge ? '14px 16px' : '10px 14px', gap: isLarge ? 14 : 10 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
          <div style={{
            fontSize: isLarge ? 20 : 16, fontWeight: 800, color: '#112D4E',
            lineHeight: 1.15, marginBottom: isLarge ? 8 : 4,
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {person.firstName}<br />{person.lastName}
          </div>
          {person.position && (
            <div style={{
              fontSize: isLarge ? 12 : 10, fontWeight: 600, color: '#3F72AF',
              textTransform: 'capitalize', marginBottom: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {person.position.replace(/_/g, ' ')}
            </div>
          )}
          <div style={{
            fontSize: isLarge ? 11 : 9, color: '#64748b', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {person.agency}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR" style={{
              width: isLarge ? 100 : 80, height: isLarge ? 100 : 80,
              borderRadius: 6, border: '1px solid #e2e8f0',
            }} />
          ) : (
            <div style={{
              width: isLarge ? 100 : 80, height: isLarge ? 100 : 80,
              borderRadius: 6, border: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#94a3b8', fontSize: 10,
            }}>QR</div>
          )}
          <div style={{ fontSize: 8, color: '#94a3b8', marginTop: 3, fontFamily: "'DM Mono', monospace", letterSpacing: 1 }}>
            {person.badgeId || person.pin}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        background: '#f8fafc', borderTop: '1px solid #e2e8f0',
        padding: isLarge ? '6px 16px' : '4px 14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: isLarge ? 9 : 7, color: '#94a3b8' }}>
          ID: {person.badgeId || `PIN-${person.pin}`}
        </span>
        <span style={{ fontSize: isLarge ? 9 : 7, color: '#94a3b8' }}>
          Scan to check in
        </span>
      </div>
    </div>
  );
}

export default function PersonnelBadges() {
  const navigate = useNavigate();
  const { personnel, loadPersonnel } = useRollCallStore();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [badgeStyle, setBadgeStyle] = useState('standard');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [qrMap, setQrMap] = useState<Record<number, string>>({});
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadPersonnel(); }, []);

  const activePersonnel = useMemo(() => personnel.filter(p => p.status === 'active'), [personnel]);

  // Pre-select if coming from a specific person
  useEffect(() => {
    const personId = searchParams.get('id');
    if (personId) setSelected(new Set([Number(personId)]));
  }, [searchParams]);

  // Generate QR codes for selected
  const selectedKeys = [...selected].sort().join(',');
  useEffect(() => {
    if (selected.size === 0) { setQrMap({}); return; }
    let cancelled = false;
    const generateQRs = async () => {
      const newMap: Record<number, string> = {};
      const people = activePersonnel.filter(p => selected.has(p.id));
      for (const person of people) {
        if (cancelled) return;
        const qrData = person.badgeId || `RC-PIN:${person.pin}`;
        try {
          newMap[person.id] = await QRCode.toDataURL(qrData, {
            width: 200, margin: 1, color: { dark: '#112D4E', light: '#ffffff' },
          });
        } catch { /* skip */ }
      }
      if (!cancelled) setQrMap(newMap);
    };
    generateQRs();
    return () => { cancelled = true; };
  }, [selectedKeys, activePersonnel]);

  const filtered = activePersonnel.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.agency.toLowerCase().includes(q);
  });

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.id)));
  };

  const badgePeople = activePersonnel.filter(p => selected.has(p.id));

  const handleExportPDF = async () => {
    if (badgePeople.length === 0) return;
    setGenerating(true);
    try {
      const dims = BADGE_DIMS[badgeStyle];
      const pdf = new jsPDF({ orientation: dims.w > dims.h ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const gap = 6;
      const cols = Math.floor((pageW - margin * 2 + gap) / (dims.w + gap));
      const rows = Math.floor((pageH - margin * 2 + gap) / (dims.h + gap));
      const perPage = cols * rows;

      for (let i = 0; i < badgePeople.length; i++) {
        if (i > 0 && i % perPage === 0) pdf.addPage();
        const pageIdx = i % perPage;
        const col = pageIdx % cols;
        const row = Math.floor(pageIdx / cols);
        const x = margin + col * (dims.w + gap);
        const y = margin + row * (dims.h + gap);
        const person = badgePeople[i];

        pdf.setDrawColor(17, 45, 78);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(x, y, dims.w, dims.h, 2, 2);

        // Header
        pdf.setFillColor(26, 54, 93);
        pdf.rect(x, y, dims.w, 10, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.text('ROLLCALL', x + 4, y + 6.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(5);
        pdf.text('Emergency Attendance', x + dims.w - 4, y + 6.5, { align: 'right' });

        // Name
        pdf.setTextColor(17, 45, 78);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(dims.fontSize.name);
        const nameY = y + 18;
        pdf.text(person.firstName, x + 4, nameY);
        pdf.text(person.lastName, x + 4, nameY + dims.fontSize.name * 0.4);

        // Position
        if (person.position) {
          pdf.setTextColor(63, 114, 175);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(dims.fontSize.detail);
          pdf.text(person.position.replace(/_/g, ' '), x + 4, nameY + dims.fontSize.name * 0.8 + 4, { maxWidth: dims.w - dims.qr - 14 });
        }

        // Agency
        pdf.setTextColor(100, 116, 139);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(dims.fontSize.org);
        const agencyY = nameY + dims.fontSize.name * 0.8 + (person.position ? 10 : 4);
        pdf.text((person.agency || '').slice(0, 35), x + 4, agencyY, { maxWidth: dims.w - dims.qr - 14 });

        // QR
        const qrData = person.badgeId || `RC-PIN:${person.pin}`;
        try {
          const qrUrl = await QRCode.toDataURL(qrData, { width: 400, margin: 1, color: { dark: '#112D4E', light: '#ffffff' } });
          pdf.addImage(qrUrl, 'PNG', x + dims.w - dims.qr - 4, y + 14, dims.qr, dims.qr);
        } catch { /* skip */ }

        // Footer
        pdf.setDrawColor(226, 232, 240);
        pdf.line(x, y + dims.h - 7, x + dims.w, y + dims.h - 7);
        pdf.setTextColor(148, 163, 184);
        pdf.setFontSize(5);
        pdf.text(`ID: ${person.badgeId || `PIN-${person.pin}`}`, x + 4, y + dims.h - 3);
        pdf.text('Scan to check in', x + dims.w - 4, y + dims.h - 3, { align: 'right' });
      }

      pdf.save(`rollcall_badges_${new Date().toISOString().split('T')[0]}.pdf`);
      notifications.show({ message: `${badgePeople.length} badge(s) exported to PDF`, color: 'green' });
    } catch (err: any) {
      notifications.show({ message: 'PDF export failed: ' + (err?.message || ''), color: 'red' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .badge-print-area, .badge-print-area * { visibility: visible !important; }
          .badge-print-area { position: absolute; left: 0; top: 0; display: flex !important; flex-wrap: wrap; gap: 8px; padding: 8px; }
          .badge-card { break-inside: avoid; page-break-inside: avoid; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/rollcall/personnel')}>Back</Button>
            <Title order={3} c="black">Personnel Badges</Title>
          </Group>
          <Group gap="xs">
            <Select data={BADGE_STYLES} value={badgeStyle} onChange={v => setBadgeStyle(v ?? 'standard')} size="xs" w={200} />
            <Button size="xs" variant="light" leftSection={<IconPrinter size={14} />}
              onClick={() => window.print()} disabled={badgePeople.length === 0}>Print</Button>
            <Button size="xs" leftSection={<IconFileTypePdf size={14} />}
              onClick={handleExportPDF} loading={generating} disabled={badgePeople.length === 0}>Export PDF</Button>
          </Group>
        </Group>

        <Paper p="md" radius="sm" withBorder mb="md">
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm"><IconUsers size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Select Personnel ({selected.size} selected)</Text>
            <Button size="xs" variant="subtle" onClick={selectAll}>
              {selected.size === filtered.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Group>

          <TextInput placeholder="Search personnel..." size="xs" mb="sm"
            value={search} onChange={e => setSearch(e.currentTarget.value)} />

          <div style={{ maxHeight: 260, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map(p => (
              <label key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                borderRadius: 6, cursor: 'pointer',
                background: selected.has(p.id) ? 'var(--mantine-color-blue-0)' : 'transparent',
              }}>
                <Checkbox checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>{p.firstName} {p.lastName}</Text>
                  <Text size="xs" c="dimmed" truncate>{p.agency} — {(p.position || 'no position').replace(/_/g, ' ')}</Text>
                </div>
                <Text size="xs" c="dimmed" ff="monospace">{p.badgeId || p.pin}</Text>
              </label>
            ))}
          </div>
        </Paper>
      </div>

      {badgePeople.length > 0 ? (
        <>
          <Text fw={600} size="sm" mb="sm" className="no-print">
            <IconId size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Badge Preview
          </Text>
          <div className="badge-print-area" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {badgePeople.map(p => (
              <BadgeCard key={p.id} person={p} style={badgeStyle} qrDataUrl={qrMap[p.id]} />
            ))}
          </div>
        </>
      ) : (
        <Paper p="xl" radius="sm" withBorder className="no-print" style={{ textAlign: 'center' }}>
          <IconId size={32} style={{ color: '#94a3b8', marginBottom: 8 }} />
          <Text fw={600} c="dimmed">Select personnel above to preview badges</Text>
          <Text size="sm" c="dimmed">Badges include name, position, agency, and a scannable QR code</Text>
        </Paper>
      )}
    </div>
  );
}
